import { useGetContentListQuery } from '@api/api';
import { TitleAlign, TitleSize } from '@api/selections';
import { ContentRequest, EpiBlock, EpiLink } from '@api/types';
import {
  BlockWrapper,
  GenericError,
  ProductCard,
  Slider,
  SliderProps,
  SmartLink,
  Title,
  Variation,
} from '@components';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Button, styled, useMediaQuery, useTheme } from '@mui/material';
import { breakpoints } from '@themes/theme.helpers';
import React from 'react';
import { SwiperSlide } from 'swiper/react';

// STYLES
//--------
const ProductListBlockWrapper = styled(Box)<ProductListComponentProps>`
  .swiper {
    text-align: center;
    ${breakpoints.up('sm')} {
      padding: 24px;
      margin: -24px;
    }
  }
  .swiper-wrapper {
    box-sizing: unset !important;
  }
  .swiper-slide {
    height: auto !important;
  }
  .swiper-pagination {
    margin-top: 40px;
  }
  .swiper-button-prev {
    margin-left: -8px;
    ${breakpoints.down('md')} {
      opacity: 0.25;
    }
  }
  .swiper-button-next {
    margin-right: -8px;
    ${breakpoints.down('md')} {
      opacity: 0.25;
    }
  }
`;

// TYPES
//-------
export type ProductListComponentProps = EpiBlock & {
  children?: React.ReactNode;
  content?: EpiBlock[];
  titleSize?: TitleSize;
  titleAlign?: TitleAlign;
  link?: EpiLink;
};

// COMPONENTS
//------------
export const ProductListComponent = ({
  title,
  content,
  children,
  titleSize = 'h1',
  titleAlign = 'center',
  link,
}: ProductListComponentProps) => {
  const theme = useTheme();
  const isBreakpointExtraLarge = useMediaQuery(theme.breakpoints.up('xl'));
  const isBreakpointLarge = useMediaQuery(theme.breakpoints.up('lg'));
  const isBreakpointSmall = useMediaQuery(theme.breakpoints.up('sm'));

  let responsiveSettings: Partial<SliderProps> = {};
  if (isBreakpointExtraLarge) {
    responsiveSettings = { slidesPerView: 4 };
  } else if (isBreakpointLarge) {
    responsiveSettings = { slidesPerView: 3 };
  } else if (isBreakpointSmall) {
    responsiveSettings = { slidesPerView: 2 };
  } else {
    responsiveSettings = { slidesPerView: 1 };
  }

  const sliderProps: SliderProps = {
    spaceBetween: 24,
    parallax: true,
    ...responsiveSettings,
  };
  const references = content
    ?.map((x) => x?.contentLink?.id)
    .filter((x) => x !== undefined) as number[];
  const mappedReferences = references?.map((x) => x + '__CatalogContent');
  const request: ContentRequest = { references: mappedReferences, expand: '*' };
  const response = useGetContentListQuery(request);
  const { data, isLoading, isSuccess, isError } = response;

  return (
    <ProductListBlockWrapper className='product-list-block'>
      {(link || title) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'nowrap',
            mb: {
              xs: 2,
              md: 4,
            },
            width: '100%',
            position: 'relative',
            justifyContent: !link ? titleAlign : undefined,
          }}
        >
          {link && titleAlign !== 'left' && <Box sx={{ flex: 1 }}></Box>}
          {title && (
            <Title variant={titleSize || 'h2'} className='product-list-block-title'>
              {title}
            </Title>
          )}
          {link && (
            <Box
              sx={{
                flex: titleAlign !== 'right' ? 1 : undefined,
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <SmartLink {...link}>
                <Button
                  color='secondary'
                  variant='secondary'
                  sx={{
                    width: 'max-content',
                  }}
                  endIcon={<ArrowForwardIcon />}
                >
                  {link.title || link.text}
                </Button>
              </SmartLink>
            </Box>
          )}
        </Box>
      )}
      {isLoading && (
        <Slider {...sliderProps}>
          {content?.map((block) => {
            const id = block?.contentLink?.id;
            return id ? (
              <SwiperSlide key={id}>
                <ProductCard isLoading={true} />
              </SwiperSlide>
            ) : null;
          })}
        </Slider>
      )}
      {isSuccess && data?.length !== 0 ? (
        <Slider {...sliderProps}>
          {data?.map((block) => {
            const id = block?.contentLink?.id;
            return id ? (
              <SwiperSlide key={id}>
                <BlockWrapper
                  disableEditPanel={true}
                  blockData={block}
                  wrapperComponent={Variation}
                />
              </SwiperSlide>
            ) : null;
          })}
        </Slider>
      ) : null}
      {isError && <GenericError />}
      {children && <Slider {...sliderProps}>{children}</Slider>}
    </ProductListBlockWrapper>
  );
};
